import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { 
  CheckCircle, 
  Printer, 
  X, 
  ShoppingBag,
  User,
  Receipt
} from 'lucide-react'

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
}

interface SaleCompletionData {
  isVisible: boolean
  billNumber: string
  customer?: Customer | null
  totalAmount: number
  change: number
}

interface MobileSaleCompletionProps {
  saleCompletionData: SaleCompletionData | null
  onPrintReceipt: () => void
  onSkipPrint: () => void
  onNewSale: () => void
  formatCurrency: (amount: number) => string
}

export const MobileSaleCompletion = ({
  saleCompletionData,
  onPrintReceipt,
  onSkipPrint,
  onNewSale,
  formatCurrency
}: MobileSaleCompletionProps) => {
  if (!saleCompletionData?.isVisible) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-white dark:bg-gray-800">
        <div className="p-4 text-center">
          {/* Success Icon */}
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          
          {/* Success Message */}
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
            Sale Complete!
          </h2>
          
          {/* Bill Number */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 mb-3">
            <div className="text-xs text-gray-600 dark:text-gray-400">Bill Number</div>
            <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
              #{saleCompletionData.billNumber}
            </div>
          </div>
          
          {/* Customer Info */}
          {saleCompletionData.customer && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <User className="w-3 h-3 text-gray-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {saleCompletionData.customer.name}
              </span>
            </div>
          )}
          
          {/* Amount Details */}
          <div className="space-y-1 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Amount:</span>
              <span className="text-lg font-bold text-green-600 dark:text-green-400">
                {formatCurrency(saleCompletionData.totalAmount)}
              </span>
            </div>
            
            {saleCompletionData.change > 0 && (
              <div className="flex justify-between items-center text-orange-600 dark:text-orange-400">
                <span className="text-sm">Change to Return:</span>
                <span className="text-sm font-bold">
                  {formatCurrency(saleCompletionData.change)}
                </span>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="space-y-2">
            <Button
              onClick={onPrintReceipt}
              className="w-full h-9 text-sm font-semibold flex items-center justify-center gap-1"
            >
              <Printer className="w-4 h-4" />
              Print Receipt
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onSkipPrint}
                className="flex-1 h-8 text-xs flex items-center justify-center gap-1"
              >
                <X className="w-3 h-3" />
                Skip Print
              </Button>
              
              <Button
                onClick={onNewSale}
                className="flex-1 h-8 text-xs flex items-center justify-center gap-1"
              >
                <ShoppingBag className="w-3 h-3" />
                New Sale
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}